import {
    Auction,
    Bid,
    BoardReviewData,
    BonusIndicator,
    BridgePosition,
    Card,
    ForeignBoardReviewData,
    ForeignBoardReviewPlayerData,
    GamePhase,
    SeatPosition,
    SharkGameResultsV2,
    Trick,
    levels,
    strains, suitOrders,
} from '../app/types';
import { AuxiliaryMessage, Button } from '../slices/tableSlice';
import {
    Explanation,
    GameEngineInterface,
    GameResultRawData,
    ProcessorFunction,
    mapDispatchToProps, mapStateToProps,
} from './game-engine-interface.class';
import { MeetingState, SFSVAR, SFS_NAV, getSFSFalse, getSFSNumber } from './sfsVar';
import { OutputButton } from '../slices/outputSlice';
import { S3BoardData, convertS3toForeignBoardReviewData } from './s3-board-map';
import { SFSArray, SFSObject, SFSRoom, SFSUser } from 'sfs2x-api';
import { SFSConnectionStatus } from '../slices/appSlice';
import { SFS_HAND_PROPS, getMySeatData } from '../utils/mixed';
import {
    SeatPosFor,
    convertIntToSuitStr,
    convertPlayerIdToIntBridgePosition,
    fixColor,
    playerIdToBridgePosition,
    strToCall,
    swapPlayerIdToBridgePosition,
} from './game-engine-helper';
import { bridgePos, numBridgePos } from '../app/defaults';
import { bridgeWarGui, environment, gameDataHost } from '../env';
import { claimButton, concedeButton, dashboardButton, replayButton, undoButton } from './buttonsState';
import { connect } from 'react-redux';
import { convertBidToInt, convertBridgePositionToInt, convertStringToBid } from './shark-game-engine-helper';
import { decompressData } from '../utils/data-fetch';
import { engineReloadBoardReviewData } from './actions/engineCommands';
import {
    getPartnerBridgePosition,
    getSocketErrorlogin,
    getUndoConfirmPrompt,
} from '../utils/shark-helper';
import { getSFSHandState } from './handState';
import {
    lineForMinMaxValue,
    lineForPoints,
    lineForSimpleString,
    lineForSuitEndingWith,
} from './handle-bid-explanation';
import { modalGameResults, modalGeneric } from '../utils/modal.helper';
import { sfsService } from './sfs-service';
import { store } from '../app/store';
import axios from 'axios';
import moment from 'moment';
import process from './process-mid/collect';

export class SharkGameEngineInterface extends GameEngineInterface {
    private mid_processor: Record<number, ProcessorFunction> = {
        [SFSVAR.netMID.MID_NEW_DEAL]: process.MID_NEW_DEAL_SB, // 0
        [SFSVAR.netMID.MID_CURRENT_DEAL]: process.MID_CURRENT_DEAL_SB, // 1
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [SFSVAR.netMID.MID_TIME_BID]: bridgeWarGui ? process.MID_TIME_TO_BID_BWAR : process.MID_TIME_TO_BID_SB, // 5
        [SFSVAR.netMID.MID_MADE_BID]: process.MID_MADE_BID_SB, // 6
        [SFSVAR.netMID.MID_CONTRACT]: process.MID_CONTRACT, // 7
        [SFSVAR.netMID.MID_TIME_PLAY]: process.MID_TIME_TO_PLAY, // 8
        [SFSVAR.netMID.MID_MADE_PLAY]: process.MID_MADE_PLAY, // 9
        [SFSVAR.netMID.MID_TIME_CLOSE_TRICK]: process.MID_TIME_CLOSE_TRICK, // 10
        [SFSVAR.netMID.MID_TIME_SCORE]: bridgeWarGui ? process.MID_TIME_TO_SCORE_BWAR : process.MID_TIME_TO_SCORE_SB, // 11
        [SFSVAR.netMID.MID_CLAIM_RESPONSE]: process.MID_CLAIM_RESPONSE_SB, //15
        [SFSVAR.netMID.MID_CLAIM_NOTIFICATION]: process.MID_CLAIM_NOTIFICATION, //16
        [SFSVAR.netMID.MID_UNDO_RESPONSE]: process.MID_UNDO_RESPONSE, //18
        [SFSVAR.netMID.MID_UNDO_NOTIFICATION]: process.MID_UNDO_NOTIFICATION, //19
        [SFSVAR.netMID.MID_CHANGE_HAND_VISIBILITY]: process.MID_CHANGE_HAND_VISIBILITY, //25
        [SFSVAR.netMID.MID_CONTROL_ACTION]: process.MID_ACTION_CONTROL, //27
        [SFSVAR.netMID.MID_END_OF_SET]: process.MID_END_OF_SET, //36
        [SFSVAR.netMID.MID_START_ROUND]: process.MID_START_ROUND,
        [SFSVAR.netMID.MID_SET_HANDS]: process.MID_SET_HANDS,
        [SFSVAR.netMID.MID_TEXT_MESSAGE]: bridgeWarGui ? process.MID_TEXT_MESSAGE_BWAR : process.MID_TEXT_MESSAGE,
        [SFSVAR.netMID.MID_WAIT_FOR_ROUND]: process.MID_WAIT_FOR_ROUND, //44
        [SFSVAR.netMID.MID_SHOW_WELCOME]: bridgeWarGui ? process.MID_SHOW_WELCOME_BWAR : process.MID_SHOW_WELCOME, //45
        [SFSVAR.netMID.MID_SHOW_BID_EXPL]: process.MID_SHOW_BID_EXPL, //46
    };
    protected processObject = (cmdObj: SFSObject) => {
        const mid: number = cmdObj.getInt(SFSVAR.EXTENSION_MID);

        const processor = this.mid_processor[mid];
        //console.log(`Processing MID ${mid}`, cmdObj.getDump(true));
        if (processor) {
            processor(cmdObj, this); // Call the method
        } else {
            console.log(`No processor found for MID ${mid}`);
        }
    };

    protected configureSFS() {
        const useEncryption = true;
        // Create configuration object
        let hostName: string = this.props.app.urlParams?.['sfshost']?.toLowerCase() ?? '';
        const teacherName: string = this.props.app.urlParams?.['sfsteacher'] ?? '';
        if (teacherName === 'Shark Table' && hostName === 'acbl') {
            hostName = 'sfs';
        }
        const zone: string = this.props.app.urlParams?.['service'] ?? 'OnlineBridgeClub';
        this.config = {
            host: hostName === 'local' ? 'local.onlinebridge.club' : hostName + '.emabridge.com',
            port: hostName === 'local' ? 8443 : 443,
            useSSL: useEncryption,
            zone: zone,
        };
    }

    protected onLoginError = ({ errorMessage, errorCode }: { errorMessage: string; errorCode: number }) => {
        console.log('Shark Bridge onLoginError : ', errorMessage, errorCode);
        this.props.app_setEngineState({
            sfsConnectionStatus: SFSConnectionStatus.disconnected,
        });
        this.props.app_addModal(
            getSocketErrorlogin(() => {
                sfsService.sfs.disconnect();
                parent.postMessage('reloaddash', '*');
            }),
        );
    };

    protected onUserEnterRoom = ({ room, user }: { room: SFSRoom; user: SFSUser }) => {
        //this.props.showChatSendForm(room.getUserList().length > 1);
    };

    //IMPORTANT: if the user that left is ME, then move out of table to lobby, for now probably just a simple note or somethign.
    /**/
    protected onUserExitRoom = ({ room, user }: { room: SFSRoom; user: SFSUser }) => {
        // if (user.isItMe) {
        //     if (room.id !== this.sfs?.lastJoinedRoom) return;
        //     //Close the table interface and back to lobby.
        //     this.props.resetGameExcept(['jitsi', 'metaData', 'gamePhase']);
        //     this.props.setGamePhase(GamePhase.END);
        //     //this.props.resetGamePartials(['tricks', 'winner', 'contract', 'stake', 'cards', 'auction','gamePhase','gameResults']);
        //     if (room.isGame) {
        //         /*
        //         this.setState({
        //             meetingState:
        //                 this.props.app.engineStateInterface.jitsiconf !== undefined || this.props.app.engineStateInterface.twitchconf !== undefined ? MeetingState.inLobby : MeetingState.NONE
        //         });
        //         //this.props.app.engineStateInterface.jitsibreakconf = undefined;
        //         this.refreshMeeting();
        //
        //          */
        //     }
        // } else {
        //     this.props.showChatSendForm(room.getUserList().length > 1);
        // }
    };

    //This is like the onRoomJoin, but the player was actually kibitzing an is now seated at the table
    protected onSpectatorToPlayerSwitch = ({
        room,
        user,
        playerID,
    }: {
        room: SFSRoom;
        user: SFSUser;
        playerID: number;
    }) => {
        // if (!room.isGame || room !== this.sfs?.lastJoinedRoom || !user.isItMe) {
        //     return;
        // }
        // const playerRID: number = this.sfs?.mySelf.getPlayerId(room);
        // this.props.setMyBridgePosition(convertPlayerToBridgePosition(playerRID));
        // this.setState({
        //     myposition: convertPlayerToBridgePosition(playerRID)
        // });
        // this.sendRoomCommand(SFSVAR.CMD_INTERFACE_READY, new SFSObject());
    };

    protected onSpectatorToPlayerSwitchError = ({
        errorMessage,
        errorCode,
    }: {
        errorMessage: string;
        errorCode: number;
    }) => {
        //console.log('Unable to become a player due to the following error: ' + errorMessage);
    };

    //Unseat the player, it turns in Kibitz. The important action here is to adjust hand visibility based on current rules for kibitzers
    protected onPlayerToSpectatorSwitch = ({ room, user }: { room: SFSRoom; user: SFSUser }) => {
        // if (!room.isGame || room !== this.sfs?.lastJoinedRoom || user.isItMe) {
        //     return;
        // }
        // this.props.hideButtons(['undo', 'claim']);
        // this.props.setMyBridgePosition(convertIntToBridgePosition(this.props.app.engineStateInterface.currentPlayerOnServer));
        // this.setState({
        //     myposition: BridgePosition.south
        // });
        // this.sendRoomCommand(SFSVAR.CMD_INTERFACE_READY, new SFSObject());
    };

    protected onPlayerToSpectatorSwitchError = ({
        errorMessage,
        errorCode,
    }: {
        errorMessage: string;
        errorCode: number;
    }) => {
        //console.log('Unable to become a spectator due to the following error: ' + errorMessage);
    };

    protected onRoomJoined = ({ room }: { room: SFSRoom }) => {
        //console.log('Room joined successfully: ',room);
        const { app } = this.props;
        const playerID: number = sfsService.sfs?.mySelf.getPlayerId(room) ?? 0;
        switch (room.isGame) {
            case false: {
                this.props.app_updateEngineInterfaceState({
                    lobbyRoom: room,
                    competeMode: room.containsVariable(SFSVAR.SFSGAME_ROOM_ROUND),
                });
                sfsService.lobby = room;
                if (room.containsVariable('event_url')) {
                    this.returnURL = room.getVariable('event_url').value as string;
                }

                const startTime: number = sfsService.getPlayerNumber(SFSVAR.SFSGAME_ROOM_STT, undefined);
                const endTime: number = sfsService.getLobbyNumber(SFSVAR.SFSGAME_ROOM_UTC_ENDTTIME, undefined);
                this.props.app_updateEngineInterfaceState({
                    timeToGameStart: startTime > 1 ? startTime : undefined,
                    timeToGameEnd: startTime > 1 ? startTime : undefined,
                });

                if (endTime) {
                    this.props.app_setSoundPlaying('tournamentStart');
                    this.props.app_updateEngineInterfaceState({
                        timeToGameStart: undefined,
                        timeToGameEnd: endTime - 120 - Math.floor(Date.now() / 1000),
                    });
                }

                break;
            }
            case true: {
                sfsService.table = room;
                if (room.name.includes('_insta_')) {
                    this.props.app_updateEngineInterfaceState({
                        isInstaGame: true,
                    });
                }

                this.props.table_setTopBridgePosition(
                    environment === 'audrey' || bridgeWarGui
                        ? BridgePosition.north
                        : swapPlayerIdToBridgePosition(playerID ?? 4) ?? BridgePosition.north,
                );

                this.props.app_updateEngineInterfaceState({
                    isMiniBridge: room.containsVariable(SFSVAR.SFSGAME_MINIBRIDGE)
                        ? (room.getVariable(SFSVAR.SFSGAME_MINIBRIDGE).value as boolean)
                        : false,
                    playerID: playerID,
                    //                   lobbyRoom: this.props.app.engineStateInterface.lobbyRoom ?? room,
                });

                if (room.containsVariable(SFSVAR.SFSGAME_MODE)) {
                    this.props.app_updateEngineInterfaceState({
                        temp: {
                            gameMode: room.getVariable(SFSVAR.SFSGAME_MODE).value as number,
                            isBiddingQuiz: room.getVariable(SFSVAR.SFSGAME_MODE).value === SFSVAR.gameMode.BiddingQuiz,
                        },
                    });
                }

                this.props.app_updateEngineInterfaceState({
                    temp: {
                        tableID: bridgeWarGui
                            ? ''
                            : room.containsVariable(SFSVAR.SFSGAME_ROOM_DISPLAY)
                            ? ` ${room.getVariable(SFSVAR.SFSGAME_ROOM_DISPLAY).value} `
                            : `Table: ${room.id} `,
                        meetingState: this.props.app.engineStateInterface.lobbyRoom?.containsVariable(
                            SFSVAR.RVAR_MEETING_STATE,
                        )
                            ? (this.props.app.engineStateInterface.lobbyRoom.getVariable(SFSVAR.RVAR_MEETING_STATE)
                                  .value as MeetingState)
                            : MeetingState.NONE,
                    },
                });

                if (room.containsVariable(SFSVAR.SFSGAME_EXTENSION_GAME_RESULTS_V2)) {
                    try {
                        const resultV = room.getVariable(SFSVAR.SFSGAME_EXTENSION_GAME_RESULTS_V2).value as SFSObject;
                        if (resultV.containsKey('data')) {
                            const json = new TextDecoder('utf-8').decode(resultV.getByteArray('data'));
                            const game_results = JSON.parse(json) as SharkGameResultsV2;
                            this.fix_game_results(game_results);
                            this.fix_app_board_data(game_results);
                            this.props.table_setSharkGameResultsV2(game_results);
                        }
                    } catch (e) {
                        console.error('could not parse ', e);
                    }
                }

                const sfsObj = new SFSObject();
                sfsObj.putInt(SFSVAR.SFSGAME_MID_LOAD, 2);
                this.sendRoomCommand(SFSVAR.CMD_INTERFACE_READY, sfsObj);
                this.clientRequestsSettingsUpdate();
                break;
            }
        }
    };

    //This one can have data, regarding request to unblock a user, we will not deal with it now.
    protected onAdminMessage = ({ message, sender, data }: { message: any; sender: SFSUser; data: SFSObject }) => {
        const apiCall: boolean = data ? data.containsKey('sendername') : false;
        const isPop: boolean = data ? data.containsKey('popup') : false;
        if (!isPop) {
            const senderName: string = apiCall ? data.getUtfString('sendername') : 'Shark';
            if (bridgeWarGui) {
                const m: AuxiliaryMessage = {
                    text: `${message}`,
                    title: `${senderName}`,
                    titleColor: undefined,
                    buttonOk: undefined,
                    buttonCancel: bridgeWarGui ? 'Ok' : undefined,
                    buttonAuxiliary: undefined,
                };
                this.props.table_setAuxiliaryMessage({ position: bridgeWarGui ? 'center' : 'left', message: m });
            }
            else {
                this.props.table_updateTable({
                    chat:[ { timestamp: moment.utc().format(), sender: senderName, message: message as string, isSystemMessage: !apiCall }]
                });
            }
            return;
        }

        modalGeneric(`${message}`, apiCall ? data.getUtfString('sendername') : 'Shark', this.props.app_removeModal);
    };

    protected onModeratorMessage = ({ message, sender }: { message: string; sender: SFSUser }) => {
        this.props.table_updateTable({
            chat:[ {
                timestamp: moment.utc().format(),
                sender: sender.containsVariable('pn')
                        ? sender.getVariable('pn').value as string
                        : sender.containsVariable('nn')
                          ? sender.getVariable('nn').value as string
                          : sender.name,
                message: message
            }]
        });
        return;
    };
    //On a special case where there is data sent back,
    protected onPublicMessage = ({ message, sender, data }: { message: string; sender: SFSUser; data: SFSObject }) => {
        this.props.table_updateTable({
            chat:[ {
                timestamp: moment.utc().format(),
                sender: sender.containsVariable('pn')
                        ? sender.getVariable('pn').value as string
                        : sender.containsVariable('nn')
                          ? sender.getVariable('nn').value as string
                          : sender.name,
                message: message
            }]
        });

        // if (message === 'fullscreen') {
        //     this.props.setJitsiFullSize(true);
        // } else if (message === 'normalscreen') {
        //     this.props.setJitsiFullSize(false);
        // }
    };

    protected onPrivateMessage = ({ message, sender, data }: { message: string; sender: SFSUser; data: SFSObject }) => {
        console.log('Received message: ' + message);
        this.props.table_updateTable({
            chat:[ {
                timestamp: moment.utc().format(),
                sender: sender.containsVariable('pn')
                        ? sender.getVariable('pn').value as string
                        : sender.containsVariable('nn')
                          ? sender.getVariable('nn').value as string
                          : sender.name,
                message: message
            }]
        });
        // if (this.consolOut) {
        //     console.log('Received message: ' + message);
        // }
    };

    //protected process_MID_MADE_BID = process.MID_MADE_BID_SB;
    public setBridgeWarInfo(dindex?: number, bindex?: number) {
        if (sfsService.hasVar('battles')) {
            const battles = sfsService.table.getVariable('battles').value as SFSArray;
            const battle = battles.getSFSObject(battles.size() - 1);
            const duels = battles.getSFSObject(battles.size() - 1).getSFSArray('duels');
            const boardsWon = battle.getInt('wins');

            let resultWin = 0;
            let resultDraw = 0;
            let resultLoss = 0;
            for (let i = 0; i < battles.size(); i++) {
                const battle = battles.getSFSObject(i);
                const battleStatus = battle.getUtfString('status');
                if (battleStatus === 'win') {
                    resultWin++;
                } else if (battleStatus === 'loss') {
                    resultLoss++;
                } else if (battleStatus === 'draw') {
                    resultDraw++;
                }
            }
            const duelIndex = duels.size() < 3 ? duels.size() : duels.size();
            if (dindex && dindex === 1) {
                this.props.table_updateTable({
                    bridgeWarGameInfo: [`Battle ${battles.size() + 1} of 2`, `Duel ${dindex} of 3`],
                });
            } else {
                this.props.table_updateTable({
                    bridgeWarGameInfo: [`Battle ${battles.size()} of 2`, `Duel ${duelIndex} of 3`],
                });
            }
        }
    }

    public setSimpleMessage(position: 'center' | 'left' | 'right', message: string, okButton: string | undefined) {
        if (bridgeWarGui) {
            const m: AuxiliaryMessage = {
                text: message,
                title: undefined,
                titleColor: undefined,
                buttonOk: undefined,
                buttonCancel: okButton,
                buttonAuxiliary: undefined,
            };
            this.props.table_setAuxiliaryMessage({ position: position, message: m });
            return;
        }
    }

    public setTableInfo(cmdObj: SFSObject) {
        const state: SFSObject | undefined = cmdObj.getSFSObject('tablestate');

        const sIndex: number = state.containsKey('bn')
            ? state.getInt('bn') + 1
            : getSFSNumber(cmdObj, SFSVAR.SFSGAME_MID_LOAD_STATE, 0) ??
              getSFSNumber(cmdObj, SFSVAR.SFSGAME_MID_LOAD_STATE) ??
              1;

        //For bridge war this is number of battles
        const nBoards: number | undefined = sfsService.getNumber(
            SFSVAR.SFSGAME_ROOM_NB,
            sfsService.getLobbyNumber(SFSVAR.SFSGAME_ROOM_NB, undefined),
        );
        const boardInfo: object = {
            label: undefined,
            value: nBoards === 0 ? `Board ${sIndex}` : `${sIndex} of ${nBoards}`,
            boardNumber: sIndex,
        };

        if (bridgeWarGui) {
            const metaGame = sfsService.getRoomSFSObject('game_meta');
            console.log('game', metaGame.getDump(true));
            const metaRoom = sfsService.getRoomSFSObject('meta');
            console.log('room', metaRoom.getDump(true));
            const duels_per_battle = metaGame.getInt('duels_per_battle');
            const battles_per_war = metaGame.getInt('battles_per_war');
            if (duels_per_battle === 0) {
                this.props.table_updateTable({
                    bridgeWarGameInfo: [`Duel ${sIndex}`],
                });
            }else if (duels_per_battle === 1) {
                this.props.table_updateTable({
                    bridgeWarGameInfo: [`Daily Duel`],
                });
            }
            else {
                const handIndex = sIndex - 1;
                const battleIndex = Math.floor(handIndex / duels_per_battle) + 1;
                const duelIndex = (handIndex % duels_per_battle) + 1;
                this.props.table_updateTable({
                    bridgeWarGameInfo: [`Battle ${battleIndex} of ${battles_per_war}`, `Duel ${duelIndex} of ${nBoards}`],
                });
            }
        }

        const hasNext: boolean =
            (cmdObj.containsKey('hasnext') && cmdObj.getBool('hasnext')) ||
            (nBoards !== undefined && sIndex !== nBoards);
        this.props.app_updateEngineInterfaceState({
            hasNext: hasNext,
        });
        this.props.table_setBoard(boardInfo);
        this.props.table_setTableInfo({ left: undefined, right: undefined });
        this.props.table_setTableSymbol(!bridgeWarGui);
    }

    public setBiddingLadderFromState(state: SFSObject) {
        //console.log("Show Bidding box" , state.getSFSObject('biddingbox'));
        if (bridgeWarGui) {
            this.props.table_updateBiddingLadder({ isVisible: false });
            this.props.table_setComponentContainerPositions({ BiddingKeyboardComponent: 'none' });
            this.props.table_setComponentContainerPositions({ AuctionBoxComponent: 'none' });
            return;
        }

        if (!state.containsKey('biddingbox')) {
            this.props.table_updateBiddingLadder({
                isVisible: false,
            });
            this.props.table_setComponentContainerPositions({
                BiddingKeyboardComponent: 'none',
                AuctionBoxComponent: 'topLeft',
                TableSymbolComponent: 'none',
            });
            return;
        }
        const bl: SFSObject = state.getSFSObject('biddingbox');
        const stake: number = bl.getInt('stake');

        this.props.table_updateBiddingLadder({
            showArrow: bl.getBool('showarrow'),
            firstVisibleCall: strToCall(bl.containsKey('mc') ? bl.getUtfString('mc') : '7nt'),
            minimalValidCall: strToCall(bl.containsKey('mvc') ? bl.getUtfString('mvc') : '7nt'),
            isActive: bl.getBool('active'),
            isVisible: true,
            showBonusIndicator: bl.getBool('bonus'),
            bonusIndicators: [BonusIndicator.j, BonusIndicator.m, BonusIndicator.s, BonusIndicator.g],
            stake:
                stake === 4
                    ? {
                          backgroundColor: undefined,
                          call: 'rdbl',
                          disabled: false,
                          invisible: false,
                      }
                    : {
                          backgroundColor: undefined,
                          call: 'xdbl',
                          disabled: stake !== 2,
                          invisible: false,
                      },
        });

        if (bl.containsKey('hcalls')) {
            const calls: SFSArray = bl.getSFSArray('hcalls');
            //    console.log("Bidding Ladder HCalls : ", calls);
            const callsArray: Auction = [];
            for (let i = 0; i < calls.size(); i++) {
                const call: SFSObject = calls.getSFSObject(i);
                callsArray.push({
                    call: strToCall(call.getUtfString('c')),
                    backgroundColor: call.containsKey('bg') ? fixColor(call.getUtfString('bg')) : undefined,
                    key: i,
                });
            }
            //      console.log("Bidding Ladder Result : ", callsArray);
            this.props.table_updateBiddingLadderBids(callsArray);
        }
    }

    public setControlButtons(cmdObj: SFSObject | undefined) {
        //TODO: adjust control buttons
        // console.log("Control Buttons : " , cmdObj);
        // const footerButtons: TableState['footerButtons'] = [...this.props.table.footerButtons];
        // const noButtons = cmdObj === undefined;
        //Add the ever present Dashboard
        // footerButtons.push({
        //     label: noButtons || !cmdObj.containsKey('button0') ? 'Dashboard' : cmdObj.getUtfString('button0'),
        //     id: 'dashboard',
        //     value: true,
        //     highlighted: false,
        //     index: 0
        // });

        this.showButtons([dashboardButton]);
        this.showButtonsIfNeeded();
        //       this.hideButtons([prevHandButton, nextHandButton, undoButton, claimButton]);
    }

    public setContractFromSFSObject(sfsObject: SFSObject) {
        const gameState: SFSObject | undefined = sfsObject.containsKey('contract') ? sfsObject : undefined;
        const contract: SFSObject | undefined = gameState?.getSFSObject('contract')
            ? sfsObject.getSFSObject('contract')
            : sfsObject;
        //console.log("Set SB Contract", sfsObject?.getDump());
        if (!contract) {
            return;
        }

        if (bridgeWarGui) {
            this.props.table_setShowAuctionLine(false);
        } else if (
            this.props.app.engineStateInterface.isMiniBridge ||
            getSFSFalse(gameState, SFSVAR.SFSGAME_NOAUCTION)
        ) {
            this.props.table_setShowAuctionLine(false);
        } else {
            this.props.table_setShowAuctionLine(environment === 'audrey');
        }

        const showUndo = this.shouldShowUndo();
        const passedHand = contract.getBool('passed');
        const completeAuction = contract.getInt('declarer') >= 0;

        if (this.shouldShowReplay()) {
            this.showButtons([replayButton]);
        }

        if (showUndo && !passedHand) {
            //       this.showButtons([undoButton]);
        }
        this.hideButtons([claimButton, concedeButton]);

        if (passedHand || !completeAuction) {
            //sender.props.removeModal('NonBlockingSpalash');
            //We are at the end of passed out deal, the End of the hand logic should kick in, when we figured what is it
            //There being no declarer, not stakes, just 4 passes in the auction.
            if (passedHand) {
                this.props.table_updateTable({ gamePhase: GamePhase.END });
            }
            return;
        }

        // this.props.removeModal('NonBlockingSpalash');
        const declInt = contract.getInt('declarer');

        //console.log('Declarer pos', bridgePos(declInt), SeatPosFor(bridgePos(declInt), this.props.table));
        if (SeatPosFor(bridgePos(declInt), this.props.table.seatData[SeatPosition.bottom]) === SeatPosition.top) {
            // sender.props.table_setTopBridgePosition(playerIdToBridgePosition(sender.state.playerID));
        }
        const stake: number = contract.getInt('stake');
        const levelIndex: number = contract.getInt('level');
        const strainIndex: number = contract.getInt('trump'); //Use that one for proper suit rotation in the hand
        const nextPlayerBridgePosition: BridgePosition = bridgePos(contract.getInt('leader'));
        this.props.table_updateTable({
            contract: {
                level: levels[levelIndex],
                strain: strains[strainIndex],
                stake: stake as 1 | 2 | 4,
                call: strToCall(`${levelIndex}${convertIntToSuitStr(strainIndex)}`),
            },
            gamePhase: GamePhase.PLAY,
            player: nextPlayerBridgePosition,
            suitOrder: suitOrders[strainIndex],
            showTrickCounter: environment,
            declarer: bridgePos(declInt),
        });
    }

    public setHandsFromState(state: SFSObject, visibleWalkthru: boolean) {
        //console.log('Hands Data: ', state);
        const visibility: number = state.getByte(SFSVAR.SFSGAME_MID_HANDPLAYER_VISIBILITY);

        Object.values(this.props.table.seatData).forEach((value, index) => {
            this.props.table_setSeatData({
                seatPosition: value?.seatPosition ?? SeatPosition.top,
                background: undefined,
            });
            this.setHandFromState(getSFSHandState(value, state, visibility));
        });

        this.props.table_setSeatData({
            seatPosition: SeatPosition.bottom,
            labelPrimary: sfsService.getString(SFSVAR.SFSGAME_SOUTH_PLAYER_NAME, 'Robot'),
        });

        if (!bridgeWarGui) {
            this.props.table_setSeatData({
                seatPosition: SeatPosition.left,
                labelPrimary: sfsService.getString(SFSVAR.SFSGAME_WEST_PLAYER_NAME, 'Robot'),
            });
            this.props.table_setSeatData({
                seatPosition: SeatPosition.top,
                labelPrimary: sfsService.getString(SFSVAR.SFSGAME_NORTH_PLAYER_NAME, 'Robot'),
            });
            this.props.table_setSeatData({
                seatPosition: SeatPosition.right,
                labelPrimary: sfsService.getString(SFSVAR.SFSGAME_EAST_PLAYER_NAME, 'Robot'),
            });
        }

        if (state.containsKey(SFS_HAND_PROPS.center)) {
            this.setHandFromState(getSFSHandState(undefined, state, visibility));
            this.props.table_setCenterSeat(true);
        } else {
            this.props.table_setCenterSeat(false);
        }
    }

    public updateBoardData(
        extraPlayer:
            | { auction: Auction; tricks: Trick[]; winner: BridgePosition[]; declarer: BridgePosition }
            | undefined,
    ) {
        //   console.log('update SB stats', this.props.table.boardStats, this.props.table.myBoardStatKeys);
        const playerData: ForeignBoardReviewPlayerData[] = [];

        playerData.push({
            auction: this.props.table.auction,
            tricks: this.props.table.tricks,
            //      winners: this.props.table.winners,
            uuid: '',
            declarer: this.props.table.declarer,
        });
        if (extraPlayer) {
            playerData.push({
                auction: extraPlayer.auction,
                tricks: extraPlayer.tricks,
                //         winners: extraPlayer.winner,
                uuid: '',
                declarer: extraPlayer.declarer,
            });
        }

        this.props.table_setForeignBoardReviewData({
            cards: this.props.cards,
            seatData: this.props.table.seatData,
            contract: this.props.table.contract,
            gamePhase: this.props.table.gamePhase,
            vulnerable: this.props.table.vulnerable,
            showAuctionBox: this.props.table.showAuctionBox,
            boardStats: this.props.table.boardStats,
            myBoardStatKeys: this.props.table.myBoardStatKeys,
            playerData: playerData,
            declarer: this.props.table.declarer,
            dealer: this.props.table.dealer,
            //       winners: this.props.table.winners,
        });
    }

    protected deflateBoardReviewData(encodedData: string): S3BoardData {
        decompressData(encodedData);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const decompressed = decompressData(encodedData);
        return JSON.parse(decompressed) as unknown as S3BoardData;
    }

    public loadBoardReview(requestData: BoardReviewData) {
        //  console.log('loadBoardReview', requestData);
        if (!this.props.app.engineStateInterface.boardData[requestData.pair]) {
            this.fetchBoardData(requestData);
        } else {
            this.loadPlayerBoardReview(requestData);
        }
        return;
    }

    private fetchBoardData(requestData: BoardReviewData) {
     //   console.log(this.props.app.engineStateInterface.boardData[requestData.pair]);
        const result = this.props.table.sharkGameResultsV2.Pairs.find((pair) => pair.uuid === requestData.pair);
     //   console.log('lookign for pair:', result, result);
        const resultUrl = `${gameDataHost}/${requestData.game}/pairs/${requestData.pair}.json`;

        axios
            .get(resultUrl)
            .then((response) => {
                const rawData = response.data as GameResultRawData;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                const responseData =
                    rawData.compression !== undefined
                        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
                          this.deflateBoardReviewData(rawData.data as string)
                        : (response.data as unknown as S3BoardData);
                //console.log('responseData for pair:', requestData, responseData);
                this.props.app_updateEngineInterfaceState({
                    boardData: {
                        ...this.props.app.engineStateInterface.boardData,
                        [requestData.pair]: responseData,
                    },
                });
                store.dispatch(engineReloadBoardReviewData(requestData))
            })
            .catch((error) => {
                console.error(`S3 bucket failed Error:  ${resultUrl}`, error);
            });
    }

    private loadPlayerBoardReview(requestData: BoardReviewData) {
        let topPair: string | undefined = requestData.comparisonUuid;
            // ? requestData.comparisonUuid
            // : this.props.table.sharkGameResultsV2?.Pairs[0].uuid !== requestData.pair
            // ? this.props.table.sharkGameResultsV2?.Pairs[0].uuid
            // : this.props.table.sharkGameResultsV2?.Pairs[1] &&
            //   this.props.table.sharkGameResultsV2?.Pairs[1].uuid !== requestData.pair
            // ? this.props.table.sharkGameResultsV2?.Pairs[1].uuid
            // : undefined;
        const boardTraveller = this.props.app.engineStateInterface.travellerData?.[requestData.boardBid] ?? [];
        const top = [...boardTraveller].sort((a, b) => (b.s ?? -Infinity) - (a.s ?? -Infinity))[0];
        if (boardTraveller && boardTraveller.length > 0){
            if (!topPair)
            {
                topPair = top?.uuid;
            }else {
                const found = boardTraveller.find((item) => item.uuid === topPair);
                if (!found){
                    topPair = top?.uuid;
                }
            }
        }
        if (!topPair) {
             topPair = requestData?.comparisonUuid
            ? requestData.comparisonUuid
            : this.props.table.sharkGameResultsV2?.Pairs[0].uuid !== requestData.pair
            ? this.props.table.sharkGameResultsV2?.Pairs[0].uuid
            : this.props.table.sharkGameResultsV2?.Pairs[1] &&
              this.props.table.sharkGameResultsV2?.Pairs[1].uuid !== requestData.pair
            ? this.props.table.sharkGameResultsV2?.Pairs[1].uuid
            : undefined;
        }



        if (!topPair) {
            const boarddata: ForeignBoardReviewData | undefined = convertS3toForeignBoardReviewData(
                this.props.app.engineStateInterface.boardData[requestData.pair],
                requestData.boardDisplay,
                0,
            );
            boarddata.playerData[1] = {
                tricks: [],
                auction: [],
                //           winners: [],
                uuid: '',
                declarer: undefined,
            };
            this.props.table_updateTable({
                dealer: boarddata.dealer,
                vulnerable: boarddata.vulnerable,
            });
            this.props.table_setForeignBoardReviewData(boarddata);
            return;
        }

        if (!this.props.app.engineStateInterface.boardData[topPair]) {
            const resultUrl = `${gameDataHost}/${requestData.game}/pairs/${topPair}.json`;
            axios
                .get(resultUrl)
                .then((response) => {
                    const rawData = response.data as GameResultRawData;
                    const responseData =
                        rawData.compression !== undefined
                            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
                              this.deflateBoardReviewData(rawData.data as string)
                            : (response.data as unknown as S3BoardData);
                    this.props.app_updateEngineInterfaceState({
                        boardData: {
                            ...this.props.app.engineStateInterface.boardData,
                            [topPair]: responseData,
                        },
                    });
                    store.dispatch(engineReloadBoardReviewData(requestData))
                 })
                .catch((error) => {
                    console.error(`S3 bucket failed Error:  ${resultUrl}`, error);
                });
        } else {
            this.updateForegndata({
                ...requestData,
                comparisonUuid: topPair,
            });
        }
    }

    private updateForegndata(requestData: BoardReviewData) {
        // const delay = () => new Promise((resolve) => setTimeout(resolve, 0));
        // await delay();
        const boarddata: ForeignBoardReviewData | undefined = convertS3toForeignBoardReviewData(
            this.props.app.engineStateInterface.boardData[requestData.pair],
            requestData.boardDisplay,
            0,
        );

        const boarddataComp: ForeignBoardReviewData | undefined = requestData.comparisonUuid
            ? convertS3toForeignBoardReviewData(
                  this.props.app.engineStateInterface.boardData[requestData.comparisonUuid],
                  requestData.boardDisplay,
                  0,
              )
            : boarddata;

        if (boarddata?.playerData?.length > 0 && boarddataComp?.playerData?.length > 0) {
            boarddata.playerData[1] = boarddataComp.playerData[0];
        }
        this.props.table_updateTable({
            dealer: boarddata.dealer,
            vulnerable: boarddata.vulnerable,
        });
        this.props.table_setForeignBoardReviewData(boarddata);
    }

    public clientRequestsReplay = () => {
        if (!this.shouldShowReplay()) {
            //Post a message, that replay is not allowed at this time. That should be rear since replay button is not allowed. Server also checks and does nothing.
        }
        const sfsObj = new SFSObject();
        sfsObj.putBool(SFSVAR.SFSGAME_MID_LOAD, false);
        sfsObj.putInt(SFSVAR.EXTENSION_MID, SFSVAR.netMID.MID_CONTROL_ACTION);
        this.sendRoomCommand(SFSVAR.CMD_REPLAY_DEAL, sfsObj);
    };

    public clientRequestsUndo = () => {
        const id = 'undoNotification';
        this.props.app_addModal(
            getUndoConfirmPrompt(sfsService.getString('gs5') ?? '', () => {
                this.sendRoomCommand(SFSVAR.CMD_REQUEST_UNDO, new SFSObject());
            }),
        );
    };

    // Client responds request to undo
    private clientResponseUndo(response: string) {
        const sfsObj = new SFSObject();
        sfsObj.putInt(SFSVAR.EXTENSION_MID, SFSVAR.netMID.MID_UNDO_RESPONSE);
        sfsObj.putBool(SFSVAR.UNDO_RESPONSE, response === 'approve');
        sfsObj.putInt(
            SFSVAR.SFSGAME_SEAT_ID,
            convertPlayerIdToIntBridgePosition(this.props.app.engineStateInterface.playerID),
        );
        this.sendRoomCommand(SFSVAR.CMD_RESPONSE_UNDO, sfsObj);
        // this.props.removeCurrentModal();
    }

    public clientRequestsMakeCall = (bid: Bid) => {
        //console.log("Request Bid", bid);
        this.props.table_updateTable({ chat: [] });
        this.props.output_reset();
        if (!bid.call) {
            return;
        }
        this.waitingForBid = true;
        this.waitingForBidExpl = undefined;
        // const updatedAuction = [...this.props.table.auction.filter((item) => item.call !== 'q')];
        // this.props.table_setAuction(updatedAuction);
        //Disable Bidding Keyboard
        this.props.table_updateBiddingLadder({
            isActive: false,
        });
        this.clearAuxiliaryMessages();
        const sfsObj = new SFSObject();
        //IMPORTANT Call need to have Int parameter, one of  /* Call intValue consta nts */
        sfsObj.putUtfString(SFSVAR.SFSGAME_MID_LOAD, bid.call);
        sfsObj.putBool(SFSVAR.SFSGAME_MID_ROBOT, false);
        this.sendRoomCommand(SFSVAR.CMD_MAKE_CALL, sfsObj);
        this.showButtonsIfNeeded();
    };

    public clientRequestsExplCall = (bidIndex: number) => {
        this.props.table_updateTable({ chat: [] });
        if (bidIndex > this.props.table.auction.length) {
            return;
        }
        const bid = this.props.table.auction[bidIndex];
        if (bid.alertMessage) {
            this.props.table_setAuxiliaryMessage({
                position: 'left',
                message: {
                    text: `${bid.alertMessage ?? bid.explanation}`,
                    bid: bid,
                    title: `Alert!`,
                    titleColor: undefined,
                    buttonOk: undefined,
                    buttonCancel: undefined,
                    buttonAuxiliary: undefined,
                },
            });
            return;
        }
        if (!bid.explanation) {
            return;
        }
        const expl = bid.explanation as Explanation;
        if (!expl) {
            return;
        }

        const call: object | undefined = 'call' in expl ? expl['call'] : undefined;
        const hand: object | undefined = 'hand' in expl ? expl['hand'] : undefined;
        let message = '';
        if ('alert' in expl) {
            message = '</br>' + lineForSimpleString('alert', expl);
        }
        else if (call || hand) {
            message += lineForPoints(call, hand);
            if (call) {
                //   message += lineForMinMaxValue('minl', 'maxl', 'Losers: ', call, hand);
            }
            message += lineForSuitEndingWith('spade', call, hand) ?? '';
            message += lineForSuitEndingWith('heart', call, hand) ?? '';
            message += lineForSuitEndingWith('diamond', call, hand) ?? '';
            message += lineForSuitEndingWith('club', call, hand) ?? '';
            message += lineForMinMaxValue('minaces', 'maxaces', 'Aces: ', call);
            message += lineForSimpleString('distro', call);
            message += lineForSimpleString('conv', call);
            if (hand && 'misc' in hand) {
                message += lineForSimpleString('misc', hand);
            }
        }
        else {
            message = 'Natural';
        }
        this.props.table_setAuxiliaryMessage({
            position: 'left',
            message: {
                text: message,
                bid: bid,
                titleColor: undefined,
                buttonOk: undefined,
                buttonCancel: undefined,
                buttonAuxiliary: undefined,
            },
        });
    };

    public clientRequestOwnBidExplanation = (bid: Bid | undefined): void => {
        this.props.table_updateTable({ chat: [] });
        this.props.output_reset();
        // if ( this.waitingForBid ) {
        //     return;
        // }
        this.props.table_setAuxiliaryMessage({ position: 'left', message: undefined });
        if (!bid?.call) {
            return;
        }
        if (playerIdToBridgePosition(this.props.app.engineStateInterface.playerID) !== this.props.table.player) {
            return;
        }

        this.waitingForBidExpl = bid;

        const sfsObj = new SFSObject();
        //IMPORTANT Call need to have Int parameter, one of  /* Call intValue constants */
        sfsObj.putInt(SFSVAR.SFSGAME_MID_LOAD, convertBidToInt(convertStringToBid(bid.call)));
        sfsObj.putBool(SFSVAR.SFSGAME_MID_ROBOT, false);
        sfsObj.putInt(SFSVAR.SFSGAME_CURENT_PLAYER, convertBridgePositionToInt(this.props.table.player));
        this.sendRoomCommand(SFSVAR.CMD_REQUEST_MY_BID_EXP, sfsObj);
    };

    public cancelBidExplanation = () => {
        this.props.table_setAuxiliaryMessage({ position: 'left', message: undefined });
    };

    public clientRequestsMakePlay(card: Card) {
        this.props.output_reset();
        this.props.table_setWalkTrough(undefined);
        this.props.table_setAuxiliaryMessage({ position: 'left', message: undefined });
        this.props.table_setAuxiliaryMessage({ position: 'right', message: undefined });
        this.props.table_setAuxiliaryMessage({ position: 'center', message: undefined });
        this.props.table_setSeatData({
            seatPosition: SeatPosFor(card.bridgePosition, this.props.table.seatData[SeatPosition.bottom]),
            isInteractive: false,
            background: undefined,
        });
        const sfsObj = new SFSObject();
        sfsObj.putUtfString(SFSVAR.SFSGAME_MID_LOAD, card.id);
        sfsObj.putBool(SFSVAR.SFSGAME_MID_ROBOT, false);
        sfsObj.putInt(SFSVAR.SFSGAME_CURENT_PLAYER, card.bridgePosition ? numBridgePos[card.bridgePosition] ?? -1 : -1);
        this.sendRoomCommand(SFSVAR.CMD_MAKE_PLAY, sfsObj);

        this.showButtonsIfNeeded();
    }

    public clientRequestsClaim = (claim: number | undefined) => {
        if (!this.props.app.isInteractive) {
            this.clearAuxiliaryMessages();
            return;
        }
        const sfsObj = new SFSObject();
        sfsObj.putInt(SFSVAR.SFSGAME_MID_LOAD, claim ?? 0);
        sfsObj.putInt(
            SFSVAR.SFSGAME_CURENT_PLAYER,
            numBridgePos[this.props.app.engineStateInterface.currentPlayer ?? BridgePosition.south],
        );
        this.sendRoomCommand(SFSVAR.CMD_REQUEST_CLAIM, sfsObj);
        this.hideButtons([claimButton, concedeButton]);
        this.clearAuxiliaryMessages();
    };

    public clientRequestsSettingsUpdate() {
        const sfsObj = new SFSObject();
        sfsObj.putUtfString('speedCards', this.props.app.sharkSettings.playCardAnimations);
        sfsObj.putInt('trickDelay', this.props.app.sharkSettings.closeTrickDelay);
        this.sendRoomCommand(SFS_NAV.CMD_LOAD_SETTINS, sfsObj);
    }

    public showLeaderBoard() {
        this.props.output_reset();
        const _modalGameResults = modalGameResults({
            gameResults: this.props.table.sharkGameResultsV2,
            noCancel: false,
            noClickOutside: true,
            fullSize: true,
        });
        if (_modalGameResults) {
            this.props.app_addModal(_modalGameResults);
        }
    }

    private shouldShowReplay() {
        if (this.isPostMortem()) {
            return true;
        }
        return sfsService.getBoolean(SFSVAR.SFSGAME_ALLOW_REPLAY, false);
    }

    public shouldShowUndo() {
        if (bridgeWarGui) {
            if (this.props.table.tricks.at(0) === undefined) {
                return false;
            }
            return this.props.table.tricks.length >= 1 || this.props.table.tricks.at(0).cardIds.length >= 1;
        }

        if (this.props.app.engineStateInterface.playerID === 0) {
            return false;
        }

        const { gamePhase } = this.props.table;

        if (gamePhase === GamePhase.DEAL) {
            return false;
        }

        if (this.props.app.engineStateInterface.playerID <= 0) {
            return false;
        }

        if (sfsService.hasVar('gs5')) {
            const undos = sfsService.getNumber('gs5', 0);
            return undos > 0;
        }

        return !sfsService.getBoolean(SFSVAR.SFSGAME_NOUNDO, false);
    }

    public shouldShowClaim() {
        if (this.props.app.engineStateInterface.playerID === 0) {
            return false;
        }
        const { table } = this.props;
        const { gamePhase, seatData } = table;

        //     return true;

        // if (gamePhase !== GamePhase.PLAY) {
        //     return false;
        // }
        // if (this.sfs && this.sfs.mySelf.getPlayerId(this.sfs.lastJoinedRoom) <= 0) {
        //     return false;
        // }
        //
        const noclaim = sfsService.getBoolean(SFSVAR.SFSGAME_NOCLAIM, false);
        if (noclaim) {
            return false;
        }

        const mybridgeposition: BridgePosition | undefined = getMySeatData(seatData)?.bridgePosition;

        if (mybridgeposition === table.declarer || mybridgeposition === getPartnerBridgePosition(table.declarer)) {
            if (table.nstricks + table.ewtricks < 7) {
                return false;
            }
            return !noclaim;
        }
        return false;
    }

    public shouldShowConcede() {
        if (this.props.app.engineStateInterface.playerID === 0) {
            return false;
        }
        const { table } = this.props;
        const { gamePhase, seatData } = table;

        const noclaim = sfsService.getBoolean(SFSVAR.SFSGAME_NOCLAIM, false);
        if (noclaim) {
            return false;
        }

        if (gamePhase !== GamePhase.PLAY) {
            return false;
        }
        const mybridgeposition: BridgePosition | undefined = getMySeatData(seatData)?.bridgePosition;

        return !noclaim;
    }

    public processAuxiliaryButton = (button: OutputButton) => {
        if (bridgeWarGui && Array.isArray(button.value)) {
            this.clearAuxiliaryMessages();
            const b: Button = button.value[0] as Button;
            if (b.id === 'nexthand') {
                this.clientRequestsNextHand();
            }
            return;
        }
        this.clientRequestsAcceptClaim();
        return;
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(SharkGameEngineInterface);
